/*eslint no-console: 0*/

import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {FormattedMessage, injectIntl, FormattedHTMLMessage, IntlShape} from 'react-intl';
import config from '../../common/config';
import * as FeatureFlags from '../../common/util/FeatureFlags.ts';
import urlMapping from '../app/URLMapping';

import LoadingButton from '../fw/LoadingButton.react';

import InvokeChannelJobMutation from './InvokeChannelJobMutation.react';
import types from '../../common/model-api/types';

import {wrapNotFound} from '../app/NotFoundHandler.react';
import EventTracker from '../app/EventTracker';
import ActivateChannelCachingMutation from './ActivateChannelCachingMutation.react';
import {showJobNotification} from '../app/notification';
import mutationError from '../channel-defs/mutationError';
import {CACHING_STATUS_NONE, CACHING_STATUS_CACHED} from "../../common/model-api/ChannelType";
import {safeJSONparse} from "../../common/util/safeJSONparse";
import {getLastOrganisationId} from '../app/LastOrganisationId';
import { User } from '../../common/types/types';

export function onClickTryAgainCachingWithNotification(channel, intl) {
    return new Promise((resolve, reject) => {
        const data = channel;
        EventTracker.trackEvent(EventTracker.EVENT_CLICKS, {
            event: 'channel-activate-caching',
            type: 'application'
        });

        let onFailure = (transaction) => {
            mutationError(transaction);
            reject();
        };

        let onSuccess = (response) => {
            showJobNotification(intl, response.activateChannelCaching.job);

            resolve();
        };

        // To perform a mutation, pass an instance of one to `Relay.Store.commitUpdate`
        Relay.Store.commitUpdate(new ActivateChannelCachingMutation({channel: data, cache: true}), {
            onFailure,
            onSuccess
        });

    });
}

function onClickActivateCaching(channel, router, notifyUser) {
    return new Promise((resolve, reject) => {
        const data = channel;
        let activateCaching = !data.cachingStatus || data.cachingStatus === CACHING_STATUS_NONE;

        EventTracker.trackEvent(EventTracker.EVENT_CLICKS, {
            event: 'channel-activate-caching',
            type: 'application'
        });

        let onFailure = (transaction) => {
            mutationError(transaction);
            reject();
        };

        let onSuccess = (response) => {
            if (notifyUser && activateCaching) {
                showJobNotification(this.props.intl, response.activateChannelCaching.job);
            }


            let type = types['channels'];

            let path = urlMapping.getUrl(type, "view", data);
            router.push(path);

            resolve();
        };

        // To perform a mutation, pass an instance of one to `Relay.Store.commitUpdate`
        Relay.Store.commitUpdate(new ActivateChannelCachingMutation({channel: data, cache: activateCaching}), {
            onFailure,
            onSuccess
        });

    });
}

interface ActivateChannelCachingProps {
 intl?: object;
 relay?: object;
 user: User;
}

class ActivateChannelCaching extends React.Component<ActivateChannelCachingProps> {

    typeName = 'channels';

    static propTypes = {
        intl: PropTypes.object,
        relay: PropTypes.object,
        user: PropTypes.object.isRequired,
    };
    static contextTypes = {
        router: PropTypes.object
    };

    constructor() {
        super();
    }

    onClickActivateCaching = () => {
        return onClickActivateCaching(this.props.user.organisation.channel, this.context.router);
    };

    render() {
        const data = this.props.user.organisation.channel;
        let type = types[this.typeName];

        let channelDefMetadata = safeJSONparse(data.channelDef.metadata);

        let title = data.channelDef.name;
        if (data.name) {
            title += ' - ' + data.name;
        }



        let cachingCanBeActivated = FeatureFlags.hasFeatureFlag(FeatureFlags.FEATURE_ENABLE_CACHING, this.props.user) && channelDefMetadata.canStoreData;

        return (
            <AppPanel breadcrumbParams={[
                {type, data},
                {type: types['organisations'], data: this.props.user.organisation}]
            } className="ChannelView" title={title}>
                <h3><FormattedMessage defaultMessage="Activate caching" id="activateChannelCaching.title" /></h3>

                <FormattedHTMLMessage defaultMessage="<p>For some features of {productName}, it is necessary to activate caching. Caching stores data from {channelDefName} in {productName} and allows to handle advanced querying and searching.</p>" id="channelCaching.explanation" values={{productName: config.PRODUCT_NAME, channelDefName: data.channelDef.name}}/>

                {cachingCanBeActivated ?
                    <div style={{paddingTop: '1em'}}>
                        {!data.cachingStatus || data.cachingStatus === CACHING_STATUS_NONE ?
                            <LoadingButton className="btn btn-primary" onClick={this.onClickActivateCaching}><i aria-hidden="true" className="fa fa-fw fa-play"></i> <FormattedMessage defaultMessage="Activate caching mode" id="channel.activateCaching" /></LoadingButton>
                        : null}
                        {data.cachingStatus === CACHING_STATUS_CACHED ?
                            <LoadingButton className="btn btn-primary" onClick={this.onClickActivateCaching}><i aria-hidden="true" className="fa fa-fw fa-stop"></i> <FormattedMessage defaultMessage="Disable caching mode" id="activateChannel.disableCaching" /></LoadingButton>
                        : null}
                    </div>
                :
                    <div>
                        <FormattedMessage defaultMessage="Caching mode is not possible for this Application." id="application.cachingModeNotPossible" />

                    </div>
                }

            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(ActivateChannelCaching), (props) => {
    return props.user.organisation && props.user.organisation.channel;
}), {
    initialVariables: {
        organisationId: '',
        channelId: '',
    },
    prepareVariables (variables) {
        if (!variables.organisationId) {
            variables.organisationId = getLastOrganisationId();
        }

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                featureFlags,
                organisation(id: $organisationId) {
                    id,
                    realId,
                    name,
                    featureFlags,
                    channel(id: $channelId) {
                        id,
                        realId,
                        organisation_id,
                        type,
                        cachingStatus,
                        description,
                        name,
                        metadata,
                        channelDef {
                            id,
                            name,
                            description,
                            metadata,
                            icon,
                        },
                        ${ActivateChannelCachingMutation.getFragment('channel')},
                        ${InvokeChannelJobMutation.getFragment('channel')},
                    }
                }
            }
        `,
    },
});
